import { useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const DigitalCard = () => {
  const contact = {
    name: "Mihai Radulescu",
    title: "CEO",
    company: "BEE-ADVISED",
    mobile: "+40 744 324 272",
    email: "office@bee-advised.ro",
    website: "www.bee-advised.ro"
  };

  const vCard = `BEGIN:VCARD
VERSION:3.0
FN:${contact.name}
ORG:${contact.company}
TITLE:${contact.title}
TEL;TYPE=CELL:${contact.mobile}
EMAIL:${contact.email}
URL:https://${contact.website}
END:VCARD`;

  useEffect(() => {
    document.title = `${contact.name} - ${contact.company}`;
  }, []);

  const handleSaveContact = () => {
    const blob = new Blob([vCard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${contact.name.replace(' ', '_')}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A192F] via-[#0d2341] to-[#0A192F] flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#00BCD4]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00BCD4]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div 
        data-testid="digital-card-container"
        className="relative w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-3">
              <svg width="50" height="56" viewBox="0 0 44 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="13" cy="12" rx="11" ry="5" transform="rotate(-30 13 12)" fill="rgba(0,188,212,0.1)" stroke="#00BCD4" strokeWidth="1.5"/>
                <ellipse cx="31" cy="10" rx="11" ry="5" transform="rotate(30 31 10)" fill="rgba(0,188,212,0.1)" stroke="#00BCD4" strokeWidth="1.5"/>
                <polygon points="22,17 31,22 31,33 22,38 13,33 13,22" fill="#0A192F" stroke="#00BCD4" strokeWidth="1.5"/>
                <line x1="14" y1="25.5" x2="30" y2="25.5" stroke="#00BCD4" strokeWidth="1.2" opacity="0.75"/>
                <line x1="14" y1="30.5" x2="30" y2="30.5" stroke="#00BCD4" strokeWidth="1.2" opacity="0.75"/>
                <line x1="18" y1="17" x2="11" y2="5" stroke="#00BCD4" strokeWidth="1.2"/>
                <circle cx="11" cy="4.5" r="2" fill="#00BCD4"/>
                <line x1="26" y1="17" x2="33" y2="5" stroke="#00BCD4" strokeWidth="1.2"/>
                <circle cx="33" cy="4.5" r="2" fill="#00BCD4"/>
                <line x1="22" y1="38" x2="22" y2="46" stroke="#00BCD4" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="22" y1="44" x2="19" y2="41" stroke="#00BCD4" strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="22" y1="44" x2="25" y2="41" stroke="#00BCD4" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <div>
                <span className="text-[#00BCD4] font-black text-2xl tracking-tight">BEE-</span>
                <span className="text-white font-bold text-2xl tracking-tight">ADVISED</span>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <p className="text-center text-gray-400 text-sm mb-8">
            IT · Security · Smart Building Solutions
          </p>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#00BCD4]/50 to-transparent mb-8"></div>

          {/* Name & Title */}
          <div className="text-center mb-8">
            <h1 
              data-testid="contact-name"
              className="text-3xl font-bold text-white mb-2"
            >
              {contact.name}
            </h1>
            <p className="text-[#00BCD4] font-medium text-lg">{contact.title}</p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 mb-8">
            <a 
              href={`tel:${contact.mobile.replace(/\s/g, '')}`}
              data-testid="phone-link"
              className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-[#00BCD4]/10 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-full bg-[#00BCD4]/20 flex items-center justify-center group-hover:bg-[#00BCD4]/30 transition-colors">
                <svg className="w-5 h-5 text-[#00BCD4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span className="text-white font-medium">{contact.mobile}</span>
            </a>

            <a 
              href={`mailto:${contact.email}`}
              data-testid="email-link"
              className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-[#00BCD4]/10 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-full bg-[#00BCD4]/20 flex items-center justify-center group-hover:bg-[#00BCD4]/30 transition-colors">
                <svg className="w-5 h-5 text-[#00BCD4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-white font-medium">{contact.email}</span>
            </a>

            <a 
              href={`https://${contact.website}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="website-link"
              className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-[#00BCD4]/10 transition-all duration-300 group"
            >
              <div className="w-10 h-10 rounded-full bg-[#00BCD4]/20 flex items-center justify-center group-hover:bg-[#00BCD4]/30 transition-colors">
                <svg className="w-5 h-5 text-[#00BCD4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <span className="text-white font-medium">{contact.website}</span>
            </a>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white rounded-2xl">
              <QRCodeSVG 
                value={vCard}
                size={140}
                level="M"
                fgColor="#0A192F"
                data-testid="qr-code"
              />
            </div>
          </div>
          <p className="text-center text-gray-500 text-xs mb-6">
            Scanează pentru a salva contactul
          </p>

          {/* Save Contact Button */}
          <button
            onClick={handleSaveContact}
            data-testid="save-contact-btn"
            className="w-full py-4 bg-[#00BCD4] hover:bg-[#00d4ef] text-[#0A192F] font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-[#00BCD4]/25"
          >
            Salvează Contactul
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-xs mt-6">
          © {new Date().getFullYear()} BEE-ADVISED. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default DigitalCard;
